import { GENDER, PREGNANCY_OUTCOME } from './config';

const GENDER_UNKNOWN = GENDER.unknown;

// Bridge between the FHQ pregnancy shape and the Bennett/pedigree shape. Pregnancies live in
// ONE shared collection (Pregnancy base = FHQ writes, PregnancyChanged = pedigree/clinician
// writes, merged on read), but the FHQ historically stored a legacy outcome vocabulary and a
// couple of differently-named fields. These pure helpers normalise a record on READ so a
// patient-entered pregnancy renders correctly without a destructive data migration. Bennett
// values pass through unchanged, so records authored by the pedigree editor are untouched.

// Legacy FHQ outcome values → Bennett enum (config.PREGNANCY_OUTCOME).
const LEGACY_OUTCOME = {
  delivered: PREGNANCY_OUTCOME.liveBirth,
  miscarriage: PREGNANCY_OUTCOME.sab,
  termination: PREGNANCY_OUTCOME.top,
  stillbirth: PREGNANCY_OUTCOME.stillbirth
};

// Map a stored outcome (legacy OR Bennett) to the Bennett enum. Unknown / already-Bennett
// values pass through. Used by the render derivation AND the FHQ display consumers so a
// stored `delivered` reads as a live birth everywhere.
export function normalizePregnancyOutcome(outcome) {
  if (outcome == null) return outcome;
  return LEGACY_OUTCOME[outcome] || outcome;
}

const isMultiple = (v) =>
  v === true || v === 'yes' || v === 'Yes' || v === 'true';

// A pregnancy holds MANY fetuses (Desktop parity). Records written before the fetus sub-document
// existed have no `fetuses` array — their fetus-level attributes sat on the pregnancy itself, i.e.
// one pregnancy was implicitly ONE fetus. Synthesise that single fetus on READ so every consumer
// (render derivation, editors, tiles) can assume `fetuses` is always populated, and NO data
// migration is needed: legacy records keep rendering exactly as they did.
//
// The synthesised fetus deliberately carries NO `_id`; the render layer derives a node uid from the
// pregnancy id + index, so pins / custom positions / saved views keyed on the old `Pregnancy._id`
// survive untouched (see deriveReproNodes).
export function normalizeFetuses(rec, normalizedOutcome) {
  if (!rec || typeof rec !== 'object') return [];
  if (Array.isArray(rec.fetuses) && rec.fetuses.length > 0) {
    // Real fetus sub-documents: normalise each one's outcome the same way.
    return rec.fetuses.map((f) => ({
      ...f,
      outcome: normalizePregnancyOutcome(f && f.outcome)
    }));
  }
  const outcome =
    normalizedOutcome !== undefined
      ? normalizedOutcome
      : normalizePregnancyOutcome(rec.outcome);
  // Legacy shape → exactly one fetus lifted from the pregnancy's own fields.
  return [
    {
      legacySingleton: true, // provenance: synthesised, not stored
      outcome,
      gender: rec.fetalSexKnown && rec.fetalSex ? rec.fetalSex : GENDER_UNKNOWN,
      fetalSexKnown: rec.fetalSexKnown,
      sexDeterminationMethod: rec.sexDeterminationMethod,
      karyotype: rec.karyotype,
      bornMemberId: rec.bornMemberId,
      // The pregnancy-level `hidden` already suppresses the whole record upstream; a legacy
      // singleton has no separate per-fetus hide of its own.
      hideInPedigree: undefined,
      diagnoses: Array.isArray(rec.diagnoses) ? rec.diagnoses : undefined
    }
  ];
}

// Reconcile a pregnancy record to the shape deriveReproNodes expects: normalise the outcome
// and fill the render field names the FHQ stores under different keys
// (completedWeeks→gestationalWeeks, twins_triplets→multiplicity), and always expose `fetuses`.
// Non-destructive — returns a shallow copy and never drops the original fields (so FHQ display
// code that still reads completedWeeks keeps working).
export function normalizePregnancyRecord(rec) {
  if (!rec || typeof rec !== 'object') return rec;
  const gestationalWeeks =
    rec.gestationalWeeks != null && rec.gestationalWeeks !== ''
      ? rec.gestationalWeeks
      : rec.completedWeeks;
  const multiplicity =
    rec.multiplicity != null
      ? rec.multiplicity
      : isMultiple(rec.twins_triplets)
      ? 2
      : undefined;
  const outcome = normalizePregnancyOutcome(rec.outcome);
  return {
    ...rec,
    outcome,
    ...(gestationalWeeks != null && gestationalWeeks !== ''
      ? { gestationalWeeks }
      : {}),
    ...(multiplicity != null ? { multiplicity } : {}),
    fetuses: normalizeFetuses(rec, outcome)
  };
}
